import axios, { AxiosResponse } from "axios";
import store from "./store";
import config from "@/config";
import eventBus from "@/eventBus";
import { EVENTS } from "@/constants/events";

declare module "vue/types/vue" {
  interface Vue {
    $axios: typeof axios;
  }
}

const AUTH_BASE_URL = config.authUri;
const API_BASE_URL = config.apiUri;

const instance = axios.create({
  baseURL: `${API_BASE_URL}/api/settlement`,
  timeout: 30000,
});
const authInstance = axios.create({
  baseURL: `${AUTH_BASE_URL}`,
  timeout: 10000,
});

const RECOVERY_ERROR_CODES = new Set([
  "RECOVERY_PENDING",
  "D1_CONFLICT",
  "D1_STORE_FAILED",
  "RECOVERY_MANUAL_REVIEW",
  "RESOLUTION_RECHECK_REQUIRED",
  "IDENTITY_MISMATCH_PENDING",
  "RENEWAL_IN_PROGRESS",
  "PREPARED_STATE_AMBIGUOUS",
]);
const RECOVERY_RETRY_DELAYS_MS = [1500, 3000] as const;

interface AuthTokens {
  access_token?: string;
  refresh_token?: string;
}

let activeRefreshPromise: Promise<AuthTokens | null> | null = null;

const wait = (durationMs: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, durationMs));

const getResponseErrorCode = (error: any): string | undefined =>
  error?.response?.data?.code ||
  error?.response?.data?.error?.code ||
  (error?.response?.data?.error === "TOKEN_RECOVERY_PENDING"
    ? "RECOVERY_PENDING"
    : undefined);

const isRecoveryError = (error: any): boolean =>
  error?.response?.status === 409 &&
  RECOVERY_ERROR_CODES.has(getResponseErrorCode(error) || "");

const isRequestCanceled = (error: any): boolean =>
  error?.code === "ERR_CANCELED" ||
  error?.name === "CanceledError" ||
  error?.name === "AbortError";

const showRecoveryPending = (): void => {
  store.commit("setAuthError", "recovery-pending");
  eventBus.$emit(EVENTS.SHOW_SNACKBAR, {
    message: "카페24 인증 정보를 복구 중입니다. 잠시 후 다시 시도해주세요.",
  });
};

export async function refreshAccessToken(): Promise<AuthTokens | null> {
  if (!store.state.refresh_token) return null;
  if (activeRefreshPromise) return activeRefreshPromise;

  activeRefreshPromise = (async () => {
    for (
      let attempt = 0;
      attempt <= RECOVERY_RETRY_DELAYS_MS.length;
      attempt += 1
    ) {
      try {
        const response = await authInstance.post<AuthTokens>(
          "/client/refresh",
          {
            refresh_token: store.state.refresh_token,
          }
        );
        store.commit("setTokens", response.data);
        store.commit("setAuthError", null);
        return response.data;
      } catch (error) {
        if (isRecoveryError(error)) {
          if (attempt < RECOVERY_RETRY_DELAYS_MS.length) {
            await wait(RECOVERY_RETRY_DELAYS_MS[attempt]);
            continue;
          }
          showRecoveryPending();
          return null;
        }

        const status = (error as any)?.response?.status;
        if (status === 401 || status === 403) {
          await store.dispatch("invalidateSession", "session-expired");
        } else {
          eventBus.$emit(EVENTS.SHOW_SNACKBAR, {
            message:
              "인증 갱신 서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.",
          });
        }
        return null;
      }
    }
    return null;
  })();

  try {
    return await activeRefreshPromise;
  } finally {
    activeRefreshPromise = null;
  }
}

instance.interceptors.request.use(
  (reqConfig) => {
    const { access_token } = store.state;
    if (access_token) {
      reqConfig.headers.Authorization = `Bearer ${access_token}`;
    }
    return reqConfig;
  },
  (error) => Promise.reject(error)
);

instance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    if (isRequestCanceled(error)) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshed = await refreshAccessToken();
      if (refreshed?.access_token) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${refreshed.access_token}`;
        return instance(originalRequest);
      }
      return Promise.reject(error);
    } else {
      const status = error.response?.status;
      if (isRecoveryError(error)) {
        showRecoveryPending();
        return Promise.reject(error);
      }
      const message =
        status === 403
          ? "접근 권한이 없습니다."
          : status >= 500
          ? "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요."
          : "네트워크 연결 오류가 발생했습니다.";

      eventBus.$emit(EVENTS.SHOW_SNACKBAR, { message });
    }
    return Promise.reject(error);
  }
);

export default instance;
